/**
 * Charte de chaque organisme pour ce qui SORT du CRM : pages publiques
 * (questionnaires, signature de convention, évaluation de lieu, espace
 * formateur) et aperçus de mails. L'interface du CRM garde son propre design
 * system (tokens.js) : ce fichier ne la concerne pas.
 *
 * Miroir exact de php/App/Brand/TenantBrand.php — garder les deux identiques.
 * Couleurs des logos (arbitrage du 2026-11-04) :
 *   - Apteed : marine #1b2445 (en-têtes, titres), vert #23a976 (boutons, accents)
 *   - Onedys : bleu #227eb7 (en-têtes, titres, boutons), turquoise #00b6be (accents)
 * Les autres tenants reçoivent une charte neutre, jamais celle d'un autre organisme.
 */

export const BRAND_NEUTRALS = {
  text: '#1a1d29',
  textMuted: '#55575d',
  textLight: '#94a3b8',
  pageBg: '#f8f9fb',
  cardBg: '#ffffff',
  border: '#e6e6e6',
  buttonText: '#ffffff'
};

export const TENANT_BRANDS = {
  apteed: {
    name: 'APTEED',
    primary: '#1b2445',
    button: '#23a976',
    buttonHover: '#1d8f63',
    accent: '#23a976',
    accentSoft: '#e8f5f0',
    accentBorder: '#bfe6d6',
    accentDark: '#065f46',
    link: '#23a976',
    logoUrl: 'https://apteed.fr/wp-content/uploads/2024/04/Apteed-logotype-1.png'
  },
  onedys: {
    name: 'ONEDYS',
    primary: '#227eb7',
    button: '#227eb7',
    buttonHover: '#1b6a9c',
    accent: '#00b6be',
    accentSoft: '#e6f4f9',
    accentBorder: '#bfe3ef',
    accentDark: '#155a85',
    link: '#227eb7',
    logoUrl: 'https://onedys.fr/lovable-uploads/622e1a28-d330-4f3a-9199-c1ab89a37702.png'
  }
};

export const NEUTRAL_BRAND = {
  name: '',
  primary: '#334155',
  button: '#475569',
  buttonHover: '#334155',
  accent: '#64748b',
  accentSoft: '#f1f5f9',
  accentBorder: '#e2e8f0',
  accentDark: '#1e293b',
  link: '#475569',
  logoUrl: ''
};

/**
 * Tenant courant : paramètre `?tenant=` (liens publics envoyés par mail),
 * sinon sous-domaine — même règle que AgreementSignPublic et formationFormats.
 */
export const brandTenant = () => {
  if (typeof window === 'undefined') return '';
  const fromQuery = new URLSearchParams(window.location.search).get('tenant');
  const raw = (fromQuery || (window.location.hostname || '').split('.')[0] || '').toLowerCase();
  if (raw === 'apteed_crm') return 'apteed';
  return raw.replace(/^syna_/, '');
};

/** Charte complète (rôles de marque + neutres) d'un tenant, courant par défaut. */
export const getTenantBrand = (tenant) => {
  const t = (tenant || brandTenant() || '').toLowerCase();
  return { ...BRAND_NEUTRALS, ...(TENANT_BRANDS[t] || NEUTRAL_BRAND), tenant: t };
};

/**
 * Assombrit une couleur vers le noir (`amount` ∈ [0, 1]) : fonds sombres
 * dérivés de la couleur de marque (rail de l'espace formateur, écran d'accueil).
 * Helper front seulement — pas de miroir PHP nécessaire.
 */
export const brandShade = (hex, amount) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  const k = 1 - Math.max(0, Math.min(1, amount));
  const ch = (v) => Math.round(v * k).toString(16).padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
};

/** `#23a976` + 0.1 → `rgba(35, 169, 118, 0.1)`. */
export const brandAlpha = (hex, alpha) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
